import { FC, Fragment, useMemo } from 'react';

import styled, { css } from 'styled-components';

import { PaletteColor } from '@utils/types';
import { getCurrencySymbol } from '@utils/utils';

export type PriceFormat = 'discount' | 'normal' | 'landing' | 'payment';

export type PriceProps = {
  price: number;
  currency?: string;
  color?: PaletteColor;
  originalPrice?: number;
  discountedPrice?: number;
  sellingPrice?: number;
  originalPriceSize?: number;
  Wrapper?: React.ComponentType;
  format?: PriceFormat;
  fontSize?: number;
  centered?: boolean;
  isExpedia?: boolean;
  currencyFontSize?: number;
  showDecimal?: boolean;
  currencyFontWeight?: number;
  decimalCurrencyFlexDirection?: 'column' | 'row';
  decimalFontSize?: number;
  originalPriceCurrency?: string;
  isTour?: boolean;
  tourType?: string;
  tourListing?: boolean;
  reservationSuccess?: boolean;
  orginalDiscountedPrice?: number;
  horizontalAlign?: 'flex-start' | 'center' | 'flex-end';
};

const DiscountedPriceContainer = styled.span<{
  size: number;
}>`
  color: ${({ theme }) => theme.palette.pinkSwan};
  text-transform: uppercase;
  font-size: ${({ size }) => size}px;
  letter-spacing: 0.6px;
  font-weight: 600;
`;
const OriginalPriceContainer = styled.span<{ size: number; noLine?: boolean }>`
  color: ${({ theme }) => theme.palette.pinkSwan};
  text-decoration: ${({ noLine }) => (noLine ? 'none' : 'line-through')};
  text-transform: uppercase;
  font-size: ${({ size }) => size}px;
  letter-spacing: 0.6px;
  font-weight: 600;
`;

const TourOriginalPriceContainer = styled.span<{
  size: number;
  noLine?: boolean;
}>`
  color: ${({ theme }) => theme.palette.pinkSwan};
  text-decoration: ${({ noLine }) => !noLine && 'line-through'};
  text-transform: uppercase;
  font-size: ${({ size }) => size}px;
  letter-spacing: 0.6px;
  font-weight: 600;
`;

const Decimals = styled.span`
  font-weight: 600;
`;
const Currency = styled.span<{
  fontSize?: number;
  fontWeight?: number;
}>`
  text-transform: uppercase;
  margin-left: 3px;
  font-size: ${(p) => (p.fontSize ? `${p.fontSize}px` : 'inherit')};
  font-weight: ${(p) => (p.fontWeight ? p.fontWeight : 'inherit')};
`;
const DecimalsCurrency = styled.div<{
  decimalCurrencyFlexDirection: 'row' | 'column';
}>`
  flex-direction: ${(p) => p.decimalCurrencyFlexDirection}; ;
`;

const shouldApplyOpacity = (
  format: PriceFormat,
  allowedValues: PriceFormat[]
) => allowedValues.indexOf(format) > -1;
const Container = styled.div<{
  color: PaletteColor;
  format: PriceFormat;
  centered: boolean;
  decimalFontSize: number;
}>`
  color: ${({ theme, color }) => theme.palette[color]};
  font-size: ${(p) => p.decimalFontSize}px;
  font-weight: 600;
  display: flex;
  align-items: ${(p) => (p.centered ? 'center' : 'unset')};
  opacity: ${(p) => (shouldApplyOpacity(p.format, ['payment']) ? 0.75 : 1)};
  ${DecimalsCurrency} {
    margin-left: 3px;
    display: flex;
    align-self: center;
    opacity: ${(p) =>
      shouldApplyOpacity(p.format, ['discount', 'payment']) ? 0.75 : 1};
  }
`;
const Whole = styled.span<{ fontSize: number }>`
  font-size: ${(p) => p.fontSize}px;
  font-weight: 900;
  line-height: 1;
`;

const WithOriginalPrice = styled.div<{
  horizontalAlign?: 'flex-start' | 'center' | 'flex-end';
}>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ horizontalAlign }) => horizontalAlign || 'flex-end'};
  align-items: flex-end;

  & > :first-child {
    width: 100%;
    display: flex;
    justify-content: ${({ horizontalAlign }) => horizontalAlign || 'flex-end'};
  }

  ${Container} {
    flex: 1;

    ${({ horizontalAlign }) =>
      horizontalAlign &&
      css`
        justify-content: ${horizontalAlign};
      `}

    ${Whole} {
      line-height: 1;
    }
  }
`;

const toLocalFormat = (price: number, local = 'tr-TR') =>
  (+price.toFixed(2)).toLocaleString(local);
export const getNumberParts = (price: number, local = 'tr-TR') => {
  const [whole = '0', decimals = '00'] = toLocalFormat(price, local).split(',');
  return [whole, decimals.padEnd(2, '0')];
};

export const OriginalPrice: FC<{
  price?: number;
  currency: string;
  size?: number;
  noLine?: boolean;
  centered?: boolean;
  className?: string;
  tourType?: string;
  isTour?: boolean;
  tourListing?: boolean;
  milePrice?: number;
  control?: boolean;
}> = ({
  price,
  currency,
  size = 12,
  noLine,
  className,
  isTour = false,
  tourListing,
  control,
}) => {
  if (price === undefined) return null;
  const [whole, decimals] = getNumberParts(price);
  if (isTour) {
    return (
      <div className={className || ''}>
        {tourListing ? (
          <TourOriginalPriceContainer size={size} noLine={noLine}>
            {`${whole},${decimals} ${currency}`}
          </TourOriginalPriceContainer>
        ) : (
          <OriginalPriceContainer size={size} noLine={control}>
            {`${whole},${decimals} ${getCurrencySymbol(currency)}`}
          </OriginalPriceContainer>
        )}
      </div>
    );
  } else {
    return (
      <div className={className || ''}>
        {noLine ? (
          `${whole},${decimals} ${getCurrencySymbol(currency)}`
        ) : (
          <OriginalPriceContainer size={size}>
            {`${whole},${decimals} ${currency}`}
          </OriginalPriceContainer>
        )}
      </div>
    );
  }
};

export const DiscountedPrice: FC<{
  price?: number;
  currency: string;
  size?: number;
  noLine?: boolean;
  centered?: boolean;
  className?: string;
  tourType?: string;
  isTour?: boolean;
}> = ({ price, currency, size = 12, noLine, className, isTour = false }) => {
  if (price === undefined) return null;
  const [whole, decimals] = getNumberParts(price);
  if (isTour) {
    return (
      <div className={className || ''}>
        <DiscountedPriceContainer size={size}>
          {`${whole},${decimals} ${getCurrencySymbol(currency)}`}
        </DiscountedPriceContainer>
      </div>
    );
  } else {
    return (
      <div className={className || ''}>
        {noLine ? (
          `${whole},${decimals} ${getCurrencySymbol(currency)}`
        ) : (
          <DiscountedPriceContainer size={size}>
            {`${whole},${decimals} ${getCurrencySymbol(currency)}`}
          </DiscountedPriceContainer>
        )}
      </div>
    );
  }
};

const Price: FC<PriceProps> = ({
  price,
  currency = 'TL',
  color = 'darkGrey',
  originalPrice,
  discountedPrice,
  sellingPrice,
  originalPriceSize,
  Wrapper = originalPrice ? WithOriginalPrice : Fragment,
  format = 'normal',
  fontSize = 28,
  isExpedia = false,
  currencyFontSize,
  currencyFontWeight = 600,
  showDecimal = true,
  decimalCurrencyFlexDirection = 'column',
  decimalFontSize = 12,
  isTour = false,
  originalPriceCurrency,
  tourListing,
  reservationSuccess,
  orginalDiscountedPrice,
  ...rest
}) => {
  const [whole, decimal] = getNumberParts(sellingPrice ? sellingPrice : price);
  const hasDiscount = useMemo(
    () => originalPrice !== price,
    [originalPrice, price]
  );

  return (
    <Wrapper {...rest}>
      {isExpedia && reservationSuccess ? (
        <>
          {hasDiscount ? (
            <OriginalPrice
              tourListing={tourListing}
              isTour={isTour}
              size={originalPriceSize}
              price={originalPrice}
              currency={getCurrencySymbol(originalPriceCurrency) || currency}
              noLine={isExpedia}
            />
          ) : null}
        </>
      ) : null}
      {isExpedia ? (
        <>
          {reservationSuccess === undefined ? (
            <>
              {hasDiscount ? (
                <OriginalPrice
                  tourListing={tourListing}
                  isTour={
                    orginalDiscountedPrice &&
                    orginalDiscountedPrice != originalPrice
                      ? true
                      : false
                  }
                  size={originalPriceSize}
                  price={originalPrice}
                  currency={
                    getCurrencySymbol(originalPriceCurrency) || currency
                  }
                  noLine={isExpedia}
                />
              ) : null}
              {orginalDiscountedPrice != originalPrice ? (
                <DiscountedPrice
                  size={originalPriceSize}
                  price={orginalDiscountedPrice}
                  currency={
                    getCurrencySymbol(originalPriceCurrency) || currency
                  }
                  noLine={isExpedia}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <>
          {reservationSuccess === undefined ? (
            <>
              {hasDiscount ? (
                <OriginalPrice
                  tourListing={tourListing}
                  isTour={isTour}
                  size={originalPriceSize}
                  price={originalPrice}
                  currency={
                    getCurrencySymbol(originalPriceCurrency) || currency
                  }
                  control={originalPrice === discountedPrice}
                  noLine={isExpedia}
                />
              ) : null}
            </>
          ) : null}
        </>
      )}
      {originalPrice != discountedPrice && discountedPrice != sellingPrice ? (
        <DiscountedPrice
          isTour={isTour}
          size={originalPriceSize}
          price={discountedPrice}
          currency={getCurrencySymbol(originalPriceCurrency) || currency}
          noLine={isExpedia}
        />
      ) : null}
      <Container
        color={color}
        format={format}
        centered
        decimalFontSize={decimalFontSize}
      >
        <Whole fontSize={fontSize}>{whole}</Whole>
        <DecimalsCurrency
          decimalCurrencyFlexDirection={decimalCurrencyFlexDirection}
        >
          {showDecimal ? <Decimals>,{decimal}</Decimals> : null}
          <Currency fontSize={currencyFontSize} fontWeight={currencyFontWeight}>
            {getCurrencySymbol(currency) || currency}
          </Currency>
        </DecimalsCurrency>
      </Container>
    </Wrapper>
  );
};

export default Price;
